import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const CATEGORIES = [
  'All',
  'Nutrients & Chemicals',
  'Seeds & Seedlings',
  'Growing Media',
  'Growing Systems',
  'Infrastructure & Structure',
  'Electrical & Solar',
  'Equipment & Spare Parts',
  'Packaging Materials',
  'Harvested Produce',
]

const CATEGORY_ICONS = {
  'Nutrients & Chemicals': '🧪',
  'Seeds & Seedlings': '🌱',
  'Growing Media': '🪨',
  'Growing Systems': '💧',
  'Infrastructure & Structure': '🔩',
  'Electrical & Solar': '⚡',
  'Equipment & Spare Parts': '🔧',
  'Packaging Materials': '📦',
  'Harvested Produce': '🌾',
  'Equipment & Spare Parts': '🔧',
  'Harvested Produce': '🌾',
}

export default function Inventory() {
  const { user, profile: _profile, isAdmin: _isAdmin } = useAuth()
  const [balances, setBalances] = useState([])
  const [movements, setMovements] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('All')
  const [activeTab, setActiveTab] = useState('stock')  // stock | movements | add-item
  const [movementModal, setMovementModal] = useState(null)  // null | {type: 'in'|'out'|'adjustment', item}
  const [newItemModal, setNewItemModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  // Movement form
  const [moveForm, setMoveForm] = useState({ quantity: '', reason: '', cost_per_unit: '', supplier: '', movement_date: ugandaDate() })

  // New stock item form
  const [newItem, setNewItem] = useState({ name: '', category: 'Nutrients & Chemicals', unit: 'litres', reorder_level: '', reorder_qty: '', supplier: '', notes: '' })

  useEffect(() => {
    loadBalances()
    loadMovements()
  }, [])

  const loadBalances = async () => {
    setLoading(true)
    const { data } = await supabase.from('stock_balances').select('*')
    setBalances(data || [])
    setLoading(false)
  }

  const loadMovements = async () => {
    const { data } = await supabase
      .from('stock_movements')
      .select('*, stock_items(name, unit, category), profiles(full_name)')
      .order('created_at', { ascending: false })
      .limit(100)
    setMovements(data || [])
  }

  const filtered = activeCategory === 'All'
    ? balances
    : balances.filter(b => b.category === activeCategory)

  const lowStock = balances.filter(b => b.needs_reorder)

  const handleMovement = async () => {
    if (!moveForm.quantity || !movementModal) return
    setSaving(true)
    const { error } = await supabase.from('stock_movements').insert({
      stock_item_id: movementModal.item.id,
      user_id: user.id,
      movement_type: movementModal.type,
      quantity: parseFloat(moveForm.quantity),
      unit: movementModal.item.unit,
      movement_date: moveForm.movement_date,
      reason: moveForm.reason,
      cost_per_unit: moveForm.cost_per_unit ? parseFloat(moveForm.cost_per_unit) : null,
      supplier: moveForm.supplier,
    })
    if (error) { setMsg({ type: 'error', text: error.message }) }
    else {
      setMsg({ type: 'success', text: `${movementModal.type === 'in' ? 'Stock received' : movementModal.type === 'out' ? 'Stock used/issued' : 'Adjustment'} recorded successfully.` })
      setMovementModal(null)
      setMoveForm({ quantity: '', reason: '', cost_per_unit: '', supplier: '', movement_date: ugandaDate() })
      await loadBalances()
      await loadMovements()
    }
    setSaving(false)
    setTimeout(() => setMsg(null), 4000)
  }

  const handleAddItem = async (e) => {
    e.preventDefault()
    setSaving(true)
    const { error } = await supabase.from('stock_items').insert(newItem)
    if (error) { setMsg({ type: 'error', text: error.message }) }
    else {
      setMsg({ type: 'success', text: `${newItem.name} added to inventory.` })
      setNewItemModal(false)
      setNewItem({ name: '', category: 'Nutrients & Chemicals', unit: 'litres', reorder_level: '', reorder_qty: '', supplier: '', notes: '' })
      await loadBalances()
    }
    setSaving(false)
    setTimeout(() => setMsg(null), 4000)
  }

  const movementTypeLabel = { in: 'Stock In (Received)', out: 'Stock Out (Used)', adjustment: 'Adjustment' }
  const movementTypeColor = { in: 'var(--green-mid)', out: 'var(--red)', adjustment: 'var(--amber)' }

  return (
    <AppLayout title="Inventory & Stores">
      <div className="section-header">
        <div className="section-icon">📦</div>
        <div className="section-info">
          <h2>Inventory &amp; Stores</h2>
          <p>Live stock balances across all categories. Every movement is logged with a running total.</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setNewItemModal(true)}>+ Add Stock Item</button>
        </div>
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="alert alert-red" style={{ marginBottom: 16 }}>
          ⚠️ <strong>{lowStock.length} item{lowStock.length > 1 ? 's' : ''} need reordering:</strong>{' '}
          {lowStock.map(i => i.name).join(', ')}
        </div>
      )}

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 18 }}>
        <div className="kpi green">
          <div className="kpi-label">Total Items Tracked</div>
          <div className="kpi-value">{balances.length}</div>
          <div className="kpi-note">Across {CATEGORIES.length - 1} categories</div>
        </div>
        <div className={`kpi ${lowStock.length > 0 ? 'red' : ''}`}>
          <div className="kpi-label">Low / Reorder Now</div>
          <div className="kpi-value">{lowStock.length}</div>
          <div className="kpi-note">{lowStock.length === 0 ? 'All levels OK' : 'Action needed'}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Movements This Week</div>
          <div className="kpi-value">{movements.filter(m => {
            const d = new Date(m.created_at)
            const week = new Date(); week.setDate(week.getDate() - 7)
            return d > week
          }).length}</div>
          <div className="kpi-note">In + out combined</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Last Updated</div>
          <div className="kpi-value" style={{ fontSize: '1rem', marginTop: 4 }}>
            {movements[0] ? new Date(movements[0].created_at).toLocaleDateString('en-UG') : '—'}
          </div>
          <div className="kpi-note">Most recent movement</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => setActiveTab('stock')}>📦 Current Stock</button>
        <button className={`tab ${activeTab === 'movements' ? 'active' : ''}`} onClick={() => setActiveTab('movements')}>📋 Movement Log</button>
        <button className={`tab ${activeTab === 'dispatch' ? 'active' : ''}`} onClick={() => setActiveTab('dispatch')}>🚚 Dispatch to Delivery</button>
        <button className={`tab ${activeTab === 'wip' ? 'active' : ''}`} onClick={() => setActiveTab('wip')}>🔄 WIP Accounting</button>
      </div>

      {activeTab === 'stock' && (
        <>
          {/* Category filter */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer',
                  border: `1.5px solid ${activeCategory === cat ? 'var(--amber)' : 'var(--border)'}`,
                  background: activeCategory === cat ? 'var(--amber-pale)' : 'white',
                  color: activeCategory === cat ? 'var(--amber)' : 'var(--text-muted)',
                  fontWeight: activeCategory === cat ? 700 : 400,
                  fontFamily: "'Josefin Sans', sans-serif"
                }}
              >
                {cat !== 'All' && CATEGORY_ICONS[cat] + ' '}{cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner dark" /></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Balance</th>
                    <th>Unit</th>
                    <th>Reorder At</th>
                    <th>Status</th>
                    <th>Last Movement</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={8}>
                      <div className="empty-state">
                        <div className="empty-icon">📦</div>
                        <h3>No items in this category</h3>
                      </div>
                    </td></tr>
                  ) : filtered.map(item => (
                    <tr key={item.id} style={{ background: item.needs_reorder ? '#fff8f0' : undefined }}>
                      <td style={{ fontWeight: 600 }}>{item.name}</td>
                      <td>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {CATEGORY_ICONS[item.category]} {item.category}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          fontFamily: "'Cormorant Garamond', serif",
                          fontSize: '1.15rem',
                          fontWeight: 700,
                          color: item.needs_reorder ? 'var(--red)' : item.current_balance > item.reorder_level * 2 ? 'var(--green-mid)' : 'var(--amber)'
                        }}>
                          {parseFloat(item.current_balance).toFixed(2)}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{item.unit}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                        {item.reorder_level > 0 ? `${item.reorder_level} ${item.unit}` : '—'}
                      </td>
                      <td>
                        {item.needs_reorder
                          ? <span className="badge badge-red">⚠️ Reorder Now</span>
                          : <span className="badge badge-green">✓ OK</span>}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.last_movement_date ? new Date(item.last_movement_date).toLocaleDateString('en-UG') : 'Never'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ color: 'var(--green-mid)', borderColor: 'var(--green-mid)' }}
                            onClick={() => { setMovementModal({ type: 'in', item }); setMoveForm(f => ({ ...f, movement_date: ugandaDate() })) }}
                          >
                            + In
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                            onClick={() => { setMovementModal({ type: 'out', item }); setMoveForm(f => ({ ...f, movement_date: ugandaDate() })) }}
                          >
                            − Out
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'movements' && (
        <div className="card">
          <div className="card-title">Stock Movement Log</div>
          <div className="card-subtitle">Every stock in, stock out, and adjustment — newest first</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Item</th><th>Type</th><th>Quantity</th><th>Reason</th><th>Logged By</th></tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr><td colSpan={6}>
                    <div className="empty-state"><div className="empty-icon">📋</div><h3>No movements yet</h3><p>Use the Stock In / Stock Out buttons to record movements.</p></div>
                  </td></tr>
                ) : movements.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontSize: '0.78rem' }}>{new Date(m.movement_date).toLocaleDateString('en-UG')}</td>
                    <td style={{ fontWeight: 600 }}>{m.stock_items?.name || '—'}</td>
                    <td>
                      <span className={`badge ${m.movement_type === 'in' ? 'badge-green' : m.movement_type === 'out' ? 'badge-red' : 'badge-amber'}`}>
                        {m.movement_type === 'in' ? '↑ In' : m.movement_type === 'out' ? '↓ Out' : '~ Adj'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, color: m.movement_type === 'in' ? 'var(--green-mid)' : m.movement_type === 'out' ? 'var(--red)' : 'var(--amber)' }}>
                        {m.movement_type === 'out' ? '−' : '+'}{m.quantity} {m.unit}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)', maxWidth: 200 }}>{m.reason || '—'}</td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.profiles?.full_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock Movement Modal */}
      {activeTab === 'dispatch' && <DispatchTab user={user} />}
      {activeTab === 'wip' && <WIPTab user={user} />}

      {movementModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMovementModal(null)}>
          <div className="modal">
            <div className="modal-title" style={{ color: movementTypeColor[movementModal.type] }}>
              {movementModal.type === 'in' ? '📥' : movementModal.type === 'out' ? '📤' : '⚖️'} {movementTypeLabel[movementModal.type]}
            </div>
            <div className="modal-subtitle">
              <strong>{movementModal.item.name}</strong> · Current balance: <strong>{parseFloat(movementModal.item.current_balance).toFixed(2)} {movementModal.item.unit}</strong>
            </div>

            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={moveForm.movement_date} onChange={e => setMoveForm(f => ({ ...f, movement_date: e.target.value }))} />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Quantity ({movementModal.item.unit}) *</label>
                <input className="form-input" type="number" step="0.01" placeholder="0" value={moveForm.quantity} onChange={e => setMoveForm(f => ({ ...f, quantity: e.target.value }))} autoFocus />
              </div>
              {movementModal.type === 'in' && (
                <div className="form-group">
                  <label className="form-label">Cost per {movementModal.item.unit} (UGX)</label>
                  <input className="form-input" type="number" placeholder="Optional" value={moveForm.cost_per_unit} onChange={e => setMoveForm(f => ({ ...f, cost_per_unit: e.target.value }))} />
                </div>
              )}
            </div>
            {movementModal.type === 'in' && (
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <input className="form-input" placeholder="Supplier name" value={moveForm.supplier} onChange={e => setMoveForm(f => ({ ...f, supplier: e.target.value }))} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Reason / Notes *</label>
              <input className="form-input" placeholder={
                movementModal.type === 'in' ? 'e.g. Received from supplier monthly order'
                : movementModal.type === 'out' ? 'e.g. Used in lettuce tank — pH adjustment'
                : 'e.g. Physical count correction'
              } value={moveForm.reason} onChange={e => setMoveForm(f => ({ ...f, reason: e.target.value }))} />
            </div>

            {moveForm.quantity && (
              <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: '0.82rem' }}>
                New balance after this movement:{' '}
                <strong style={{ color: movementModal.type === 'in' ? 'var(--green-mid)' : 'var(--red)' }}>
                  {(parseFloat(movementModal.item.current_balance) + (movementModal.type === 'in' ? 1 : -1) * parseFloat(moveForm.quantity || 0)).toFixed(2)} {movementModal.item.unit}
                </strong>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setMovementModal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleMovement}
                disabled={saving || !moveForm.quantity}
                style={{ background: movementTypeColor[movementModal.type] }}
              >
                {saving ? <><span className="spinner" /> Saving...</> : `Record ${movementTypeLabel[movementModal.type]}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Stock Item Modal */}
      {newItemModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setNewItemModal(false)}>
          <div className="modal">
            <div className="modal-title">📦 Add New Stock Item</div>
            <div className="modal-subtitle">Add a new item to track in your inventory. You can add opening stock after.</div>
            <form onSubmit={handleAddItem}>
              <div className="form-group">
                <label className="form-label">Item Name *</label>
                <input className="form-input" placeholder="e.g. Calcium Nitrate" required value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Unit of Measure *</label>
                  <select className="form-select" value={newItem.unit} onChange={e => setNewItem(p => ({ ...p, unit: e.target.value }))}>
                    {['litres', 'ml', 'kg', 'g', 'units', 'packs', 'pairs', 'sheets', 'bottles', 'rolls'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Reorder Level (alert below this)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="0" value={newItem.reorder_level} onChange={e => setNewItem(p => ({ ...p, reorder_level: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Reorder Quantity (how much to order)</label>
                  <input className="form-input" type="number" step="0.01" placeholder="0" value={newItem.reorder_qty} onChange={e => setNewItem(p => ({ ...p, reorder_qty: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Supplier</label>
                <input className="form-input" placeholder="Main supplier name" value={newItem.supplier} onChange={e => setNewItem(p => ({ ...p, supplier: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" style={{ minHeight: 50 }} placeholder="Any storage or handling notes" value={newItem.notes} onChange={e => setNewItem(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setNewItemModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner" /> Adding...</> : '📦 Add to Inventory'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// ─── Dispatch to Delivery ──────────────────────────────────────────────────────
function DispatchTab({ user }) {
  const [produce, setProduce] = React.useState([])
  const [invoices, setInvoices] = React.useState([])
  const [dispatches, setDispatches] = React.useState([])
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState(null)
  const [form, setForm] = React.useState({
    invoice_id: '', crop_name: '', grade: 'Grade A', qty_kg: '', notes: '',
    dispatch_date: ugandaDate()
  })

  React.useEffect(() => { loadData() }, [])

  const loadData = async () => {
    const [prodRes, invRes, dispRes] = await Promise.all([
      supabase.from('stock_balances').select('*').eq('category','Harvested Produce').gt('current_balance',0),
      supabase.from('invoices').select('id,invoice_number,customer_name,balance_due').in('status',['Unpaid','Partial']).order('invoice_date',{ascending:false}),
      supabase.from('stores_dispatch').select('*').order('created_at',{ascending:false}).limit(30)
    ])
    setProduce(prodRes.data||[])
    setInvoices(invRes.data||[])
    setDispatches(dispRes.data||[])
  }

  const selectedProduce = produce.find(p => p.name === form.crop_name)
  const available = selectedProduce ? parseFloat(selectedProduce.current_balance) : 0
  const overDispatch = parseFloat(form.qty_kg||0) > available

  const handleDispatch = async () => {
    if (!form.crop_name || !form.qty_kg) { setMsg({type:'error',text:'Crop and quantity are required.'}); return }
    if (overDispatch) { setMsg({type:'error',text:`Cannot dispatch more than available stock (${available.toFixed(2)} kg).`}); return }
    setSaving(true); setMsg(null)

    // Insert dispatch record
    const { error } = await supabase.from('stores_dispatch').insert({
      dispatched_by: user.id,
      dispatch_date: form.dispatch_date,
      invoice_id: form.invoice_id || null,
      crop_name: form.crop_name,
      grade: form.grade,
      qty_kg: parseFloat(form.qty_kg),
      notes: form.notes,
    })
    if (error) { setMsg({type:'error',text:error.message}); setSaving(false); return }

    // Deduct from stock via stock_movement
    if (selectedProduce) {
      await supabase.from('stock_movements').insert({
        stock_item_id: selectedProduce.id,
        user_id: user.id,
        movement_type: 'out',
        quantity: parseFloat(form.qty_kg),
        unit: selectedProduce.unit||'kg',
        movement_date: form.dispatch_date,
        reason: `Dispatched to delivery${form.invoice_id?' — Invoice ref':''}`
      })
    }

    setMsg({type:'success',text:`${form.qty_kg}kg of ${form.crop_name} dispatched to delivery. Stock updated.`})
    setSaving(false)
    setForm(f=>({...f,qty_kg:'',notes:'',invoice_id:''}))
    loadData()
    setTimeout(()=>setMsg(null),4000)
  }

  return (
    <div>
      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="alert alert-amber" style={{marginBottom:16}}>
        🚚 <strong>Dispatch to Delivery</strong> — When a sale is ready to go out, stores signs off the crop here. This deducts from Stores and records it for the delivery team. A delivery note should be created in Logistics.
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-title">Dispatch Produce from Stores</div>

        {produce.length === 0 ? (
          <div className="alert alert-red">⚠️ No harvested produce currently in stores. Record a harvest first.</div>
        ) : (
          <>
            <div className="grid-2" style={{marginBottom:14}}>
              <div className="form-group">
                <label className="form-label">Dispatch Date</label>
                <input className="form-input" type="date" value={form.dispatch_date} onChange={e=>setForm(f=>({...f,dispatch_date:e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Linked Invoice (optional)</label>
                <select className="form-select" value={form.invoice_id} onChange={e=>setForm(f=>({...f,invoice_id:e.target.value}))}>
                  <option value="">— Select invoice —</option>
                  {invoices.map(inv=><option key={inv.id} value={inv.id}>{inv.invoice_number} — {inv.customer_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Crop *</label>
                <select className="form-select" value={form.crop_name} onChange={e=>setForm(f=>({...f,crop_name:e.target.value}))}>
                  <option value="">— Select crop —</option>
                  {produce.map(p=>(
                    <option key={p.id} value={p.name}>{p.name} — {parseFloat(p.current_balance).toFixed(2)} {p.unit||'kg'} available</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Grade</label>
                <select className="form-select" value={form.grade} onChange={e=>setForm(f=>({...f,grade:e.target.value}))}>
                  {['Grade A','Grade B','Mixed'].map(g=><option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">
                  Qty to Dispatch (kg) *
                  {form.crop_name && (
                    <span style={{marginLeft:8,fontSize:'0.72rem',fontFamily:"'DM Mono',monospace",
                      color: overDispatch?'var(--red)':'var(--green-mid)',
                      background: overDispatch?'var(--red-pale)':'var(--green-pale)',
                      padding:'2px 7px',borderRadius:4
                    }}>
                      {overDispatch ? `⚠️ Max: ${available.toFixed(2)} kg` : `Available: ${available.toFixed(2)} kg`}
                    </span>
                  )}
                </label>
                <input className="form-input" type="number" step="0.1" placeholder="0"
                  value={form.qty_kg}
                  onChange={e=>setForm(f=>({...f,qty_kg:e.target.value}))}
                  style={{borderColor: overDispatch?'var(--red)':undefined}}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-input" placeholder="e.g. For Restaurant Jade delivery" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleDispatch} disabled={saving||overDispatch}>
              {saving?<><span className="spinner"/>Dispatching...</>:'🚚 Dispatch from Stores'}
            </button>
          </>
        )}
      </div>

      <div className="card">
        <div className="card-title">Recent Dispatches</div>
        {dispatches.length===0
          ? <div className="empty-state"><div className="empty-icon">🚚</div><h3>No dispatches yet</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Crop</th><th>Grade</th><th>Qty (kg)</th><th>Invoice</th><th>Notes</th></tr></thead>
                <tbody>
                  {dispatches.map(d=>(
                    <tr key={d.id}>
                      <td style={{fontSize:'0.78rem'}}>{new Date(d.dispatch_date).toLocaleDateString('en-UG')}</td>
                      <td style={{fontWeight:600}}>{d.crop_name}</td>
                      <td style={{fontSize:'0.78rem'}}>{d.grade}</td>
                      <td style={{fontFamily:"'DM Mono',monospace",fontWeight:600}}>{parseFloat(d.qty_kg).toFixed(2)}</td>
                      <td style={{fontSize:'0.78rem',fontFamily:"'DM Mono',monospace",color:'var(--text-muted)'}}>{d.invoice_id?'Linked':'—'}</td>
                      <td style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>{d.notes||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  )
}

// ─── WIP ACCOUNTING TAB ────────────────────────────────────────────────────────
// Raw Materials → WIP (when nutrients used on crops) → Finished Goods (when harvested)
function WIPTab({ user }) {
  const [wipEntries, setWipEntries] = React.useState([])
  const [wipSummary, setWipSummary] = React.useState({ rawMaterials: 0, wip: 0, finishedGoods: 0 })
  const [loading, setLoading] = React.useState(true)
  const [showForm, setShowForm] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState(null)
  const [stockItems, setStockItems] = React.useState([])
  const [form, setForm] = React.useState({
    entry_date: ugandaDate(),
    type: 'raw_to_wip',
    stock_item_id: '',
    item_name: '',
    quantity: '',
    unit: '',
    unit_cost: '',
    crop_name: '',
    notes: '',
  })

  React.useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [wipRes, stockRes] = await Promise.all([
      supabase.from('wip_journal').select('*, profiles(full_name)').order('entry_date', { ascending: false }).limit(50),
      supabase.from('stock_items').select('id,name,unit,category').eq('is_active', true),
    ])
    const entries = wipRes.data || []
    setWipEntries(entries)
    setStockItems(stockRes.data || [])

    // Calculate running balances
    const raw = entries.filter(e => e.type === 'purchase_to_raw').reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0)
    const toWip = entries.filter(e => e.type === 'raw_to_wip').reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0)
    const toFinished = entries.filter(e => e.type === 'wip_to_finished').reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0)
    const sold = entries.filter(e => e.type === 'finished_to_cogs').reduce((s, e) => s + (parseFloat(e.total_cost) || 0), 0)

    setWipSummary({
      rawMaterials: raw - toWip,
      wip: toWip - toFinished,
      finishedGoods: toFinished - sold,
      cogs: sold,
    })
    setLoading(false)
  }

  const handleItemSelect = (itemId) => {
    const item = stockItems.find(s => s.id === itemId)
    setForm(f => ({ ...f, stock_item_id: itemId, item_name: item?.name || '', unit: item?.unit || '' }))
  }

  const totalCost = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_cost) || 0)

  const handleSave = async () => {
    if (!form.item_name || !form.quantity || !form.unit_cost) {
      setMsg({ type: 'error', text: 'Item, quantity and unit cost are required.' }); return
    }
    setSaving(true); setMsg(null)
    const { error } = await supabase.from('wip_journal').insert({
      user_id: user.id,
      entry_date: form.entry_date,
      type: form.type,
      stock_item_id: form.stock_item_id || null,
      item_name: form.item_name,
      quantity: parseFloat(form.quantity),
      unit: form.unit,
      unit_cost: parseFloat(form.unit_cost),
      total_cost: totalCost,
      crop_name: form.crop_name,
      notes: form.notes,
    })
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
    setMsg({ type: 'success', text: 'WIP journal entry recorded.' })
    setSaving(false); setShowForm(false); loadData()
    setTimeout(() => setMsg(null), 3000)
  }

  const typeLabels = {
    purchase_to_raw: { label: 'Purchase → Raw Materials', color: 'var(--blue)', icon: '📥' },
    raw_to_wip: { label: 'Raw Materials → WIP', color: 'var(--amber)', icon: '🔄' },
    wip_to_finished: { label: 'WIP → Finished Goods', color: 'var(--green-mid)', icon: '✅' },
    finished_to_cogs: { label: 'Finished Goods → Cost of Sales', color: 'var(--red)', icon: '💸' },
  }

  return (
    <div>
      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* Explanation */}
      <div className="alert alert-amber" style={{ marginBottom: 16 }}>
        📚 <strong>WIP (Work in Progress) Accounting:</strong> When you buy nutrients, they enter <strong>Raw Materials</strong>. When you apply them to crops, they move to <strong>WIP</strong>. When crops are harvested, they become <strong>Finished Goods</strong>. When sold, they become <strong>Cost of Sales (COGS)</strong>. This gives you a proper cost of production.
      </div>

      {/* Inventory flow summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Raw Materials', value: wipSummary.rawMaterials, color: 'var(--blue)', icon: '📦', desc: 'Purchased, not yet used' },
          { label: 'Work in Progress', value: wipSummary.wip, color: 'var(--amber)', icon: '🌱', desc: 'Applied to growing crops' },
          { label: 'Finished Goods', value: wipSummary.finishedGoods, color: 'var(--green-mid)', icon: '🌾', desc: 'Harvested, ready to sell' },
          { label: 'Cost of Sales', value: wipSummary.cogs, color: 'var(--red)', icon: '💸', desc: 'Sold this period' },
        ].map(({ label, value, color, icon, desc }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', fontWeight: 700, color }}>UGX {Math.max(0, value || 0).toLocaleString()}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Flow diagram */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap', padding: 16, background: 'var(--cream)', borderRadius: 10 }}>
        {['📦 Raw Materials', '→', '🌱 WIP', '→', '🌾 Finished Goods', '→', '💸 COGS'].map((item, i) => (
          <span key={i} style={{ fontWeight: item === '→' ? 400 : 700, color: item === '→' ? 'var(--text-muted)' : 'var(--green-deep)', fontSize: item === '→' ? '1.2rem' : '0.85rem' }}>{item}</span>
        ))}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="card-title">WIP Journal Entries</div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}>+ New Entry</button>
        </div>

        {showForm && (
          <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid var(--amber)' }}>
            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">Movement Type *</label>
                <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="purchase_to_raw">📥 Purchase → Raw Materials (when you receive supplies)</option>
                  <option value="raw_to_wip">🔄 Raw Materials → WIP (when nutrients applied to crops)</option>
                  <option value="wip_to_finished">✅ WIP → Finished Goods (when crop is harvested)</option>
                  <option value="finished_to_cogs">💸 Finished Goods → Cost of Sales (when sold)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input className="form-input" type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Stock Item</label>
                <select className="form-select" value={form.stock_item_id} onChange={e => handleItemSelect(e.target.value)}>
                  <option value="">— Select or type below —</option>
                  {stockItems.filter(s => s.category === 'Nutrients & Chemicals' || s.category === 'Seeds & Seedlings').map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {!form.stock_item_id && (
                  <input className="form-input" style={{ marginTop: 6 }} placeholder="Or type item name" value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Crop (if applicable)</label>
                <select className="form-select" value={form.crop_name} onChange={e => setForm(f => ({ ...f, crop_name: e.target.value }))}>
                  <option value="">— All crops / N/A —</option>
                  {['Lettuce', 'Strawberries', 'Mixed'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input className="form-input" type="number" step="0.01" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Unit</label>
                <input className="form-input" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="e.g. kg, L, ml" />
              </div>
              <div className="form-group">
                <label className="form-label">Unit Cost (UGX)</label>
                <input className="form-input" type="number" value={form.unit_cost} onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Total Cost</label>
                <div style={{ padding: '10px 12px', background: 'var(--green-pale)', borderRadius: 6, fontWeight: 700, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', color: 'var(--green-deep)' }}>
                  UGX {totalCost.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight: 50 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Entry'}</button>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
        : wipEntries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔄</div>
            <h3>No WIP entries yet</h3>
            <p>Start by recording a "Purchase → Raw Materials" entry when you receive nutrients or seeds.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Movement</th><th>Item</th><th>Crop</th><th>Qty</th><th>Unit Cost</th><th>Total Cost</th><th>By</th></tr>
              </thead>
              <tbody>
                {wipEntries.map(e => {
                  const t = typeLabels[e.type] || {}
                  return (
                    <tr key={e.id}>
                      <td style={{ fontSize: '0.78rem' }}>{new Date(e.entry_date).toLocaleDateString('en-UG')}</td>
                      <td><span style={{ fontSize: '0.72rem', fontWeight: 700, color: t.color }}>{t.icon} {t.label}</span></td>
                      <td style={{ fontWeight: 600 }}>{e.item_name}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{e.crop_name || '—'}</td>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.8rem' }}>{e.quantity} {e.unit}</td>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.8rem' }}>{parseFloat(e.unit_cost || 0).toLocaleString()}</td>
                      <td style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{parseFloat(e.total_cost || 0).toLocaleString()}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{e.profiles?.full_name || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
